import { useEffect, useMemo, useState } from 'react';
import { FaStar } from 'react-icons/fa';
import { HashLink } from 'react-router-hash-link';
import proof from '../assets/proof.png';
import trading_img from '../assets/trading_img.png';
import AboutSection from '../components/AboutSection';
import ContactSection from '../components/ContactSection';
import Experience from '../components/Experience';
import Footer from '../components/Footer';
import JourneyTimeline from '../components/JourneyTimeline';
import Partners from '../components/Partners';
import PricingSection from '../components/PricingSection';
import Testimonial from '../components/Testimonial';
import WhoCanJoin from '../components/WhoCanJoin';

const IOS_URL = 'https://apps.apple.com/us/app/its-all-about-the-options/id6741738762';
const ANDROID_URL = 'https://play.google.com/apps/internaltest/4701567087476213772';
const WEB_APP_URL = 'https://itsallaboutheoptions.com/market-data';
const APP_STORE_BADGE = `${process.env.PUBLIC_URL}/badges/app-store.svg`;
const PLAY_STORE_BADGE = `${process.env.PUBLIC_URL}/badges/play-store.svg`;

const AppStoreBadge = ({ href }) => (
  <a href={href} target="_blank" rel="noreferrer" className="inline-block transition hover:opacity-90 active:scale-[0.98]">
    <img
      src={APP_STORE_BADGE}
      alt="Download on the App Store"
      className="h-10 w-auto sm:h-11"
      loading="lazy"
    />
  </a>
);

const PlayStoreBadge = ({ href }) => (
  <a href={href} target="_blank" rel="noreferrer" className="inline-block transition hover:opacity-90 active:scale-[0.98]">
    <img
      src={PLAY_STORE_BADGE}
      alt="Get it on Google Play"
      className="h-10 w-auto sm:h-11"
      loading="lazy"
    />
  </a>
);

const TICKER = [
  'CVNA SEP15 55C · entry 1.54 · sl 0.90 · t 3.60',
  'BIDU SEP15 150C · entry 1.15 · sl 0.60 · t 2.05',
  'AFRM SEP15 19.5C · entry 0.82 · sl 0.50 · t 1.70',
  'GOOGL SEP8 131P · entry 0.79 · sl 0.45 · t 2.05',
  'ROKU SEP15 87C · entry 1.20 · sl 0.60 · t 2.05',
  'DDOG SEP15 101C · entry 1.15 · sl 0.70 · t 2.40',
];

const EARNINGS = [
  {
    tag: 'SOXL',
    bias: 'Bearish lean',
    tone: 'rose',
    text: '37P buy write · AMD ITM 160C/170C due for pullback',
    delay: '0s',
  },
  {
    tag: 'INTC',
    bias: 'Flat call',
    tone: 'amber',
    text: '55C + 42P OTM strangle — sneaky trader calling flat',
    delay: '0.35s',
  },
  {
    tag: 'V / WDC',
    bias: 'Mixed flow',
    tone: 'violet',
    text: '300C/270P · 63C/68C spreads · KLAC 830C/780C',
    delay: '0.7s',
  },
  {
    tag: 'SPY',
    bias: 'Put lean',
    tone: 'rose',
    text: 'Put leaning 4B MOC · AXP 195C · BMI 125C ITM bearish',
    delay: '1.05s',
  },
  {
    tag: 'TSLA',
    bias: 'Neutral',
    tone: 'emerald',
    text: 'Flat likely · IBM & NOW 800C · LRCX bearish',
    delay: '1.4s',
  },
];

const EARNINGS_TONE = {
  rose: {
    badge: 'bg-rose-500/10 text-rose-600 ring-rose-500/15',
    dot: 'bg-rose-500',
    glow: 'rgba(244,63,94,0.22)',
    bar: '#f43f5e',
    chip: 'bg-rose-50 text-rose-600',
  },
  amber: {
    badge: 'bg-amber-500/10 text-amber-600 ring-amber-500/15',
    dot: 'bg-amber-500',
    glow: 'rgba(245,158,11,0.22)',
    bar: '#f59e0b',
    chip: 'bg-amber-50 text-amber-700',
  },
  violet: {
    badge: 'bg-[#6756FE]/10 text-[#6756FE] ring-[#6756FE]/15',
    dot: 'bg-[#6756FE]',
    glow: 'rgba(103,86,254,0.25)',
    bar: '#6756FE',
    chip: 'bg-[#6756FE]/10 text-[#6756FE]',
  },
  emerald: {
    badge: 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/15',
    dot: 'bg-emerald-500',
    glow: 'rgba(16,185,129,0.22)',
    bar: '#10b981',
    chip: 'bg-emerald-50 text-emerald-700',
  },
};

const STEPS = [
  {
    n: '01',
    title: 'Wake up to alerts',
    body: 'One SMS at the open. One before the close. No noise — just the trade.',
    accent: '#6756FE',
    glow: 'rgba(103,86,254,0.35)',
    tint: 'from-[#6756FE]/10 via-white to-white',
    badge: 'bg-[#6756FE]/10 text-[#6756FE]',
    ring: 'ring-[#6756FE]/20',
    delay: '0s',
  },
  {
    n: '02',
    title: 'Entry · Stop · Target',
    body: 'Every idea ships with levels. Entries. Exits. Simple.',
    accent: '#10b981',
    glow: 'rgba(16,185,129,0.32)',
    tint: 'from-emerald-500/10 via-white to-white',
    badge: 'bg-emerald-500/10 text-emerald-600',
    ring: 'ring-emerald-500/20',
    delay: '0.6s',
  },
  {
    n: '03',
    title: 'See the flow',
    body: 'Sneaky order flow + delayed earnings notes for context before you act.',
    accent: '#f59e0b',
    glow: 'rgba(245,158,11,0.3)',
    tint: 'from-amber-500/10 via-white to-white',
    badge: 'bg-amber-500/10 text-amber-600',
    ring: 'ring-amber-500/20',
    delay: '1.2s',
  },
];

const DEVICE_H = 'h-[300px] sm:h-[340px] md:h-[380px] lg:h-[400px]';

const ProofPhone = ({ className = '' }) => (
  <div className={`relative shrink-0 ${className}`}>
    <div className={`relative ${DEVICE_H} aspect-[9/19.5]`}>
      <div className="absolute -left-[3px] top-[18%] z-20 h-[5%] w-[3px] rounded-l-sm bg-[#9e9a92]" />
      <div className="absolute -left-[3px] top-[28%] z-20 h-[8%] w-[3px] rounded-l-sm bg-[#9e9a92]" />
      <div className="absolute -left-[3px] top-[38%] z-20 h-[8%] w-[3px] rounded-l-sm bg-[#9e9a92]" />
      <div className="absolute -right-[3px] top-[32%] z-20 h-[12%] w-[3px] rounded-r-sm bg-[#9e9a92]" />
      <div
        className="h-full w-full rounded-[2.2rem] p-[2.5px]"
        style={{
          background: 'linear-gradient(145deg,#e8e4dc,#b8b3a8 35%,#d4d0c8 60%,#8f8a82)',
          boxShadow: '0 28px 56px -14px rgba(15,23,42,0.3)',
        }}
      >
        <div className="h-full w-full overflow-hidden rounded-[2.05rem] bg-black p-[1.5px]">
          <div className="relative h-full w-full overflow-hidden rounded-[1.9rem] bg-white">
            <div className="absolute left-1/2 top-2 z-20 h-[20px] w-[70px] -translate-x-1/2 rounded-full bg-black" />
            <img src={proof} alt="SMS trade alerts" className="h-full w-full object-cover object-top" />
            <div className="absolute bottom-2 left-1/2 h-1 w-16 -translate-x-1/2 rounded-full bg-black/30" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const OrderFlowIpad = ({ className = '' }) => (
  <div className={`relative shrink-0 ${className}`}>
    <div className={`relative ${DEVICE_H} aspect-[1164/824]`}>
      <div
        className="relative h-full w-full rounded-[1.15rem] px-[6px] pb-[6px] pt-[12px] sm:rounded-[1.3rem] sm:px-[7px] sm:pb-[7px] sm:pt-[14px]"
        style={{
          background: 'linear-gradient(160deg,#3f3f42,#1a1a1c 45%,#0a0a0b)',
          boxShadow: '0 28px 56px -14px rgba(15,23,42,0.32), inset 0 1px 0 rgba(255,255,255,0.12)',
        }}
      >
        <div className="absolute left-1/2 top-[3px] z-10 h-2 w-2 -translate-x-1/2 rounded-full bg-[#111] ring-1 ring-white/15" />
        <div className="relative h-full w-full overflow-hidden rounded-[0.5rem] bg-black sm:rounded-[0.65rem]">
          <img
            src={trading_img}
            alt="Sneaky Order Flow"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
      </div>
    </div>
  </div>
);

const SMS_ALERTS = [
  { time: '7:56 AM', lines: ['BIDU SEP15 155C | 0.85 sl 0.60 t 2.05', 'GOOGL SEP8 131P | 0.72 sl 0.45 t 2.05'] },
  { time: '1:56 PM', lines: ['ROKU SEP15 87C | 1.20 sl 0.60 t 2.05', 'OKTA SEP16 5P | 1.30'] },
  { time: '1:57 PM', lines: ['CVNA SEP15 55C | 1.54 sl 0.90 t 3.60', 'BIDU SEP15 150C | 1.15 sl 0.60 t 2.05'] },
];

const softMaskStyle = {
  WebkitMaskImage:
    'radial-gradient(ellipse 72% 68% at 50% 48%, #000 35%, rgba(0,0,0,0.55) 58%, transparent 78%)',
  maskImage:
    'radial-gradient(ellipse 72% 68% at 50% 48%, #000 35%, rgba(0,0,0,0.55) 58%, transparent 78%)',
};

/** Soft-edged trading graph */
const AnimatedTradingGraph = () => {
  const [price, setPrice] = useState(482.35);
  const [change, setChange] = useState(1.24);
  const [bars, setBars] = useState(() =>
    Array.from({ length: 32 }, (_, i) => {
      const base = 42 + Math.sin(i / 3.2) * 16 + (i > 20 ? (i - 20) * 1.8 : 0);
      return Math.max(18, Math.min(88, base + (Math.random() * 8 - 4)));
    })
  );

  useEffect(() => {
    const priceTimer = setInterval(() => {
      setPrice((p) => {
        const delta = (Math.random() - 0.42) * 0.28;
        setChange((c) => Math.round((c + delta) * 100) / 100);
        return Math.round((p + delta) * 100) / 100;
      });
      setBars((prev) => {
        const next = prev.slice(1);
        const last = prev[prev.length - 1];
        const n = Math.max(18, Math.min(88, last + (Math.random() * 10 - 4)));
        return [...next, n];
      });
    }, 1100);
    return () => clearInterval(priceTimer);
  }, []);

  const up = change >= 0;

  const areaPath = useMemo(() => {
    const w = 100;
    const h = 100;
    const step = w / (bars.length - 1);
    const pts = bars.map((b, i) => `${i * step},${h - b}`);
    return `M0,${h} L${pts.join(' L')} L${w},${h} Z`;
  }, [bars]);

  const linePath = useMemo(() => {
    const w = 100;
    const h = 100;
    const step = w / (bars.length - 1);
    return bars.map((b, i) => `${i === 0 ? 'M' : 'L'}${i * step},${h - b}`).join(' ');
  }, [bars]);

  return (
    <div className="relative h-full w-full overflow-hidden" style={softMaskStyle}>
      <div className="relative flex h-full w-full flex-col justify-center bg-gradient-to-b from-[#f4f2ff]/90 via-white/50 to-transparent px-4 pt-4 pb-2 sm:px-6">
        <div className="relative z-10 flex items-end justify-between gap-3 px-1">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold tracking-[0.14em] text-[#6756FE] uppercase">
                Idea levels
              </span>
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                <span className="relative h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
            </div>
            <p className="mt-1.5 font-mono text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {price.toFixed(2)}
            </p>
            <p className={`mt-0.5 font-mono text-xs font-semibold sm:text-sm ${up ? 'text-emerald-600' : 'text-rose-500'}`}>
              {up ? '+' : ''}
              {change.toFixed(2)} today
            </p>
          </div>

          <div className="flex gap-2 sm:gap-2.5">
            {[
              { label: 'Entry', value: '1.54', color: 'text-emerald-600 bg-emerald-50' },
              { label: 'Stop', value: '0.90', color: 'text-rose-500 bg-rose-50' },
              { label: 'Target', value: '3.60', color: 'text-amber-600 bg-amber-50' },
            ].map((lvl) => (
              <div key={lvl.label} className={`rounded-xl px-2.5 py-1.5 text-center sm:px-3 ${lvl.color}`}>
                <p className="text-[9px] font-semibold tracking-wider uppercase opacity-70">{lvl.label}</p>
                <p className="font-mono text-xs font-bold sm:text-sm">{lvl.value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative mt-3 h-[150px] sm:h-[175px] lg:h-[190px]">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-full w-full">
            <defs>
              <linearGradient id="v2AreaSoft" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6756FE" stopOpacity="0.28" />
                <stop offset="55%" stopColor="#6756FE" stopOpacity="0.08" />
                <stop offset="100%" stopColor="#6756FE" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="v2LineSoft" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#8b7cff" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
              <filter id="v2Glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="1.2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <line x1="0" y1="58" x2="100" y2="58" stroke="#10b981" strokeWidth="0.25" strokeDasharray="1.2 1.4" opacity="0.35" />
            <line x1="0" y1="72" x2="100" y2="72" stroke="#f43f5e" strokeWidth="0.25" strokeDasharray="1.2 1.4" opacity="0.3" />
            <line x1="0" y1="32" x2="100" y2="32" stroke="#d97706" strokeWidth="0.25" strokeDasharray="1.2 1.4" opacity="0.35" />
            <path d={areaPath} fill="url(#v2AreaSoft)" className="transition-all duration-1000 ease-in-out" />
            <path
              d={linePath}
              fill="none"
              stroke="url(#v2LineSoft)"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              filter="url(#v2Glow)"
              className="transition-all duration-1000 ease-in-out"
            />
            <circle cx={100} cy={100 - bars[bars.length - 1]} r="2" fill="#10b981" opacity="0.9">
              <animate attributeName="r" values="1.6;2.4;1.6" dur="2.2s" repeatCount="indefinite" />
            </circle>
          </svg>
        </div>
      </div>
    </div>
  );
};

/** Soft-edged SMS alert scene — bubbles animate in (no phone frame) */
const AnimatedSmsScene = ({ active }) => {
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (!active) {
      setVisibleCount(0);
      return undefined;
    }
    setVisibleCount(0);
    const timers = SMS_ALERTS.map((_, i) =>
      setTimeout(() => setVisibleCount(i + 1), 400 + i * 700)
    );
    return () => timers.forEach(clearTimeout);
  }, [active]);

  return (
    <div className="relative h-full w-full overflow-hidden" style={softMaskStyle}>
      <div className="relative flex h-full w-full flex-col justify-center bg-gradient-to-b from-[#eefaf3]/80 via-white/45 to-transparent px-5 py-4 sm:px-8">
        <div className="mb-4 flex items-center gap-2 px-1">
          <span className="text-[11px] font-semibold tracking-[0.14em] text-emerald-600 uppercase">
            Trade alerts
          </span>
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
            <span className="relative h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
        </div>

        <div className="mx-auto flex w-full max-w-md flex-col gap-3">
          {SMS_ALERTS.map((msg, i) => {
            const show = i < visibleCount;
            return (
              <div
                key={msg.time}
                className={`transition-all duration-700 ease-out ${
                  show ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                }`}
              >
                <p className="mb-1.5 text-center text-[10px] font-medium tracking-wide text-slate-400">
                  {msg.time}
                </p>
                <div className="rounded-2xl rounded-bl-md bg-[#6756FE]/[0.08] px-4 py-3 shadow-sm shadow-[#6756FE]/10">
                  {msg.lines.map((line) => (
                    <p key={line} className="font-mono text-[11px] leading-relaxed text-slate-700 sm:text-xs">
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <p
          className={`mt-4 text-center text-[11px] font-medium text-slate-400 transition-opacity duration-700 ${
            visibleCount >= SMS_ALERTS.length ? 'opacity-100' : 'opacity-0'
          }`}
        >
          Entry · Stop · Target — delivered twice daily
        </p>
      </div>
    </div>
  );
};

/** Flip between trading graph and SMS scene */
const HeroVisualFlip = () => {
  const [showGraph, setShowGraph] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => setShowGraph((s) => !s), 5500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative mx-auto w-full max-w-[560px] lg:max-w-[600px] xl:max-w-[640px]">
      <div className="pointer-events-none absolute inset-[10%] rounded-full bg-[#6756FE]/12 blur-[55px]" />

      <div className="relative aspect-[16/11] w-full [perspective:1400px]">
        <div
          className="relative h-full w-full transition-transform duration-[1100ms] ease-[cubic-bezier(0.4,0.0,0.2,1)] [transform-style:preserve-3d]"
          style={{ transform: showGraph ? 'rotateY(0deg)' : 'rotateY(180deg)' }}
        >
          <div className="absolute inset-0 [backface-visibility:hidden]">
            <AnimatedTradingGraph />
          </div>
          <div
            className="absolute inset-0 [backface-visibility:hidden]"
            style={{ transform: 'rotateY(180deg)' }}
          >
            <AnimatedSmsScene active={!showGraph} />
          </div>
        </div>
      </div>

      {/* Flip indicators */}
      <div className="mt-1 flex items-center justify-center gap-2">
        <button
          type="button"
          aria-label="Show trading graph"
          onClick={() => setShowGraph(true)}
          className={`h-1.5 rounded-full transition-all ${showGraph ? 'w-5 bg-[#6756FE]' : 'w-1.5 bg-slate-300'}`}
        />
        <button
          type="button"
          aria-label="Show SMS alerts"
          onClick={() => setShowGraph(false)}
          className={`h-1.5 rounded-full transition-all ${!showGraph ? 'w-5 bg-[#6756FE]' : 'w-1.5 bg-slate-300'}`}
        />
      </div>
    </div>
  );
};

const HomeV2 = () => {
  return (
    <div className="font-v2 bg-[#f8f7fc] text-slate-800 antialiased">
      {/* ========== HERO: 30% text / 70% animated graph ========== */}
      <section id="home" className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-0 left-0 h-[50%] w-[40%] rounded-full bg-[#6756FE]/10 blur-[90px]" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 pt-10 pb-12 sm:px-6 lg:px-8 lg:pt-14 lg:pb-16">
          <div className="grid items-center gap-10 lg:grid-cols-10 lg:gap-8 xl:gap-10">
            <div className="text-center lg:col-span-3 lg:text-left">
              {/* Social proof — Stock Alarm style */}
              <div className="mb-4 flex flex-wrap items-center justify-center gap-2 lg:justify-start">
                <div className="flex items-center gap-0.5 text-amber-400">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <FaStar key={s} className="h-3 w-3" />
                  ))}
                </div>
                <p className="text-xs font-medium text-slate-500 sm:text-sm">
                  <span className="font-semibold text-slate-800">4.8</span>
                  {' · '}
                  Trusted by <span className="font-semibold text-slate-800">4,000+</span> traders
                </p>
              </div>

              <h1 className="text-[1.85rem] leading-[1.15] font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-[2.15rem] xl:text-[2.45rem]">
                The options trade alerts platform
              </h1>

              <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-slate-600 sm:text-base lg:mx-0">
                Get SMS trade ideas with entry, stop loss, and target — plus order flow context.
                Take action faster without staring at charts all day.
              </p>

              <div className="mt-6 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                <HashLink
                  smooth
                  to="/v2#pricing"
                  className="inline-flex items-center rounded-full bg-[#6756FE] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-[#6756FE]/30 transition hover:bg-[#5646e8]"
                >
                  Start free trial
                </HashLink>
                <HashLink
                  smooth
                  to="/v2#how-it-works"
                  className="inline-flex items-center rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-[#6756FE]/40 hover:text-[#6756FE]"
                >
                  See how it works →
                </HashLink>
              </div>

              {/* App download badges */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5 lg:justify-start">
                <AppStoreBadge href={IOS_URL} />
                <PlayStoreBadge href={ANDROID_URL} />
              </div>

              <p className="mt-4 text-[11px] text-slate-400 lg:text-left">
                Available on{' '}
                <a href={IOS_URL} target="_blank" rel="noreferrer" className="font-medium text-slate-600 underline-offset-2 hover:underline">
                  iOS
                </a>
                {' · '}
                <a href={ANDROID_URL} target="_blank" rel="noreferrer" className="font-medium text-slate-600 underline-offset-2 hover:underline">
                  Android
                </a>
                {' · '}
                <a href={WEB_APP_URL} target="_blank" rel="noreferrer" className="font-medium text-slate-600 underline-offset-2 hover:underline">
                  Web
                </a>
              </p>
            </div>

            <div className="relative lg:col-span-7">
              <HeroVisualFlip />
            </div>
          </div>
        </div>

        <div className="relative z-10 border-y border-slate-200 bg-[#f8f7fc]">
          <div className="flex overflow-hidden py-3">
            <div className="animate-marquee flex shrink-0 gap-10 whitespace-nowrap pr-10">
              {[...TICKER, ...TICKER].map((t, i) => (
                <span key={i} className="font-mono text-xs text-slate-500 sm:text-sm">
                  <span className="mr-2 text-emerald-500">●</span>
                  {t}
                </span>
              ))}
            </div>
            <div className="animate-marquee flex shrink-0 gap-10 whitespace-nowrap pr-10" aria-hidden>
              {[...TICKER, ...TICKER].map((t, i) => (
                <span key={`b-${i}`} className="font-mono text-xs text-slate-500 sm:text-sm">
                  <span className="mr-2 text-emerald-500">●</span>
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Partners variant="v2" />

      {/* ========== ALERTS PROOF: full-width screenshots ========== */}
      <section id="devices" className="relative overflow-hidden bg-white py-16 sm:py-20">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(103,86,254,0.08),transparent_65%)]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold tracking-[0.3em] text-[#6756FE] uppercase">Real alerts</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Entry. Stop. Target. Delivered.
            </h2>
            <p className="mt-3 text-slate-500">
              Two live SMS alerts daily — market open and before close — plus sneak peek order flow so you know what smart money is doing.
            </p>
          </div>

          <div className="mt-12 flex w-full flex-col items-center justify-center gap-12 sm:flex-row sm:items-end sm:gap-10 lg:gap-16">
            <div className="flex flex-col items-center gap-3">
              <ProofPhone />
              <p className="text-sm font-medium text-slate-500">Daily SMS trade ideas</p>
            </div>
            <div className="flex flex-col items-center gap-3">
              <OrderFlowIpad />
              <p className="text-sm font-medium text-slate-500">Sneaky order flow context</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== STEPS ========== */}
      <section id="how-it-works" className="relative overflow-hidden bg-white py-20 sm:py-28">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(103,86,254,0.07),transparent_55%)]" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs font-bold tracking-[0.3em] text-[#6756FE] uppercase">
            How it works
          </p>
          <h2 className="mx-auto mt-4 max-w-2xl text-center text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            From alert to decision in seconds
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-center text-slate-500">
            Three simple steps from SMS to context — so you’re never guessing.
          </p>

          <div className="mt-14 grid gap-6 sm:gap-7 md:grid-cols-3">
            {STEPS.map((s) => (
              <article
                key={s.n}
                className={`group relative overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-b ${s.tint} p-7 shadow-lg shadow-slate-200/40 ring-1 ${s.ring} transition duration-300 hover:-translate-y-1 hover:shadow-xl`}
              >
                {/* Soft animated color glow */}
                <div
                  className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full blur-2xl animate-glow-soft"
                  style={{ background: s.glow, animationDelay: s.delay }}
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute -bottom-12 -left-8 h-28 w-28 rounded-full blur-2xl animate-glow-soft"
                  style={{ background: s.glow, animationDelay: `calc(${s.delay} + 1.2s)` }}
                  aria-hidden
                />

                {/* Animated accent bar */}
                <div
                  className="absolute inset-x-0 top-0 h-[3px] animate-border-pulse"
                  style={{ background: `linear-gradient(90deg, transparent, ${s.accent}, transparent)`, animationDelay: s.delay }}
                  aria-hidden
                />

                <div className="relative">
                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl text-sm font-bold ${s.badge} animate-float`}
                      style={{ animationDelay: s.delay }}
                    >
                      {s.n}
                    </span>
                    <span
                      className="h-2 w-2 rounded-full animate-pulse"
                      style={{ background: s.accent, animationDelay: s.delay }}
                      aria-hidden
                    />
                  </div>

                  <h3 className="mt-6 text-xl font-bold tracking-tight text-slate-900">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500">{s.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ========== EARNINGS ========== */}
      <section className="relative overflow-hidden bg-[#f8f7fc] py-20 sm:py-28">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-0 right-0 h-[420px] w-[420px] rounded-full bg-emerald-400/10 blur-[110px]" />
          <div className="absolute bottom-0 left-0 h-[360px] w-[360px] rounded-full bg-[#6756FE]/10 blur-[100px]" />
        </div>

        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-50 px-3 py-1">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span className="text-[11px] font-bold tracking-[0.22em] text-emerald-700 uppercase">
                Market notes · delayed
              </span>
            </div>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              Upcoming Earnings
            </h2>
            <p className="mt-4 text-slate-500">
              Delayed sneak-peek context around names in focus — bias, levels, and flow notes before you act.
            </p>
          </div>

          <div className="mt-12 overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-xl shadow-slate-200/50">
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-5 py-3.5 sm:px-6">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              </div>
              <span className="font-mono text-[10px] tracking-widest text-slate-400 uppercase">
                flow · delayed feed
              </span>
            </div>

            <ul className="divide-y divide-slate-100">
              {EARNINGS.map((row) => {
                const tone = EARNINGS_TONE[row.tone];
                return (
                  <li key={row.tag} className="group relative overflow-hidden">
                    <div
                      className="pointer-events-none absolute inset-y-0 left-0 w-1 animate-border-pulse"
                      style={{ background: tone.bar, animationDelay: row.delay }}
                      aria-hidden
                    />
                    <div
                      className="pointer-events-none absolute -right-8 top-1/2 h-24 w-24 -translate-y-1/2 rounded-full blur-2xl animate-glow-soft opacity-70"
                      style={{ background: tone.glow, animationDelay: row.delay }}
                      aria-hidden
                    />

                    <div className="relative flex flex-col gap-3 px-5 py-5 transition duration-300 hover:bg-slate-50/70 sm:flex-row sm:items-start sm:gap-5 sm:px-6">
                      <div className="flex shrink-0 items-center gap-2.5 sm:w-[9.5rem] sm:flex-col sm:items-start sm:gap-2">
                        <span
                          className={`inline-flex items-center rounded-xl px-2.5 py-1.5 font-mono text-xs font-bold ring-1 ${tone.badge}`}
                        >
                          {row.tag}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide ${tone.chip}`}>
                          {row.bias}
                        </span>
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="text-sm leading-relaxed text-slate-600 sm:text-[15px]">{row.text}</p>
                        <div className="mt-2.5 flex items-center gap-2">
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${tone.dot} animate-pulse`}
                            style={{ animationDelay: row.delay }}
                            aria-hidden
                          />
                          <span className="font-mono text-[10px] tracking-widest text-slate-400 uppercase">
                            flow note
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      <section id="pricing">
        <PricingSection
          variant="v2"
          signinRedirect="/signin?redirect=/v2&tag=pricing"
        />
      </section>

      <AboutSection variant="v2" />
      <Experience variant="v2" />
      <JourneyTimeline variant="v2" contactHash="/v2#contact" />
      <Testimonial variant="v2" />
      <WhoCanJoin variant="v2" />

      <section id="contact">
        <ContactSection variant="v2" />
      </section>

      <Footer
        variant="v2"
        homeHash="/v2#home"
        aboutHash="/v2#about"
        pricingHash="/v2#pricing"
        contactHash="/v2#contact"
      />
    </div>
  );
};

export default HomeV2;
